import { PRICING_MODELS } from '@/modules/Contract/constants'
import { Contract, OtherStandardPaymentRange } from '@/modules/Contract/types'
import { toDate } from '@/utils/function'
import dayjs from 'dayjs'
import { useEffect, useMemo } from 'react'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { dateValue, emptyRange, scheduleRoot } from './utils'

export const useOtherStandardTemplate = (index: number) => {
    const formControl = useFormContext<Contract>()
    const root = scheduleRoot(index)

    const schedule = useWatch({
        control: formControl.control,
        name: root as Path<Contract>,
    }) as any

    const defaultRanges = useMemo(() => [emptyRange()], [])
    const paymentMode = schedule?.payment_mode || 'one_time'
    const paymentMethod = schedule?.payment_method || 'prepaid'
    const ranges = (
        schedule?.ranges?.length ? schedule.ranges : defaultRanges
    ) as OtherStandardPaymentRange[]

    useEffect(() => {
        if (schedule?.ranges?.length) return

        formControl.setValue(
            `${root}.payment_method` as Path<Contract>,
            'prepaid'
        )
        formControl.setValue(
            `${root}.payment_mode` as Path<Contract>,
            'one_time'
        )
        formControl.setValue(`${root}.description` as Path<Contract>, '')
        formControl.setValue(`${root}.ranges` as Path<Contract>, [emptyRange()])
        formControl.setValue(
            `order.products.[${index}].service_price_model` as Path<Contract>,
            PRICING_MODELS.Package
        )
    }, [formControl, index, root, schedule?.ranges?.length])

    useEffect(() => {
        const activeRanges =
            paymentMode === 'one_time' ? ranges.slice(0, 1) : ranges
        const total = activeRanges.reduce(
            (sum, range) => sum + Number(range.price || 0),
            0
        )
        const firstRange = activeRanges[0]
        const lastRange = activeRanges[activeRanges.length - 1]

        const setIfChanged = (
            name: Path<Contract>,
            value: string | number | boolean
        ) => {
            if (formControl.getValues(name) === value) return
            formControl.setValue(name, value as any)
        }
        const setDateIfChanged = (
            name: Path<Contract>,
            value: OtherStandardPaymentRange['start_date']
        ) => {
            if (
                dateValue(formControl.getValues(name) as any) ===
                dateValue(value)
            )
                return
            formControl.setValue(name, value as any)
        }

        setIfChanged(
            `order.products.[${index}].service_price_register` as Path<Contract>,
            total
        )
        setIfChanged(
            `order.products.[${index}].prepaid` as Path<Contract>,
            paymentMethod === 'prepaid'
        )
        setIfChanged(
            `order.products.[${index}].service_price_model` as Path<Contract>,
            PRICING_MODELS.Package
        )

        if (!firstRange || !lastRange) return

        const firstStart = firstRange.start_date
        const firstEnd = lastRange.end_date
        setDateIfChanged(
            `order.products.[${index}].service_times.first_startdate` as Path<Contract>,
            firstStart
        )
        setDateIfChanged(
            `order.products.[${index}].service_times.current_startdate` as Path<Contract>,
            firstStart
        )
        setDateIfChanged(
            `order.products.[${index}].service_times.statement_startdate` as Path<Contract>,
            firstStart
        )
        setDateIfChanged(
            `order.products.[${index}].service_times.first_enddate` as Path<Contract>,
            firstEnd
        )
        setDateIfChanged(
            `order.products.[${index}].service_times.current_enddate` as Path<Contract>,
            firstEnd
        )
        setDateIfChanged(
            `order.products.[${index}].service_times.statement_enddate` as Path<Contract>,
            firstEnd
        )
    }, [formControl, index, paymentMethod, paymentMode, JSON.stringify(ranges)])

    useEffect(() => {
        if (paymentMode !== 'one_time' || ranges.length <= 1) return
        formControl.setValue(
            `${root}.ranges` as Path<Contract>,
            ranges.slice(0, 1)
        )
    }, [formControl, paymentMode, ranges, root])

    const updateRange = (
        rangeIndex: number,
        key: keyof OtherStandardPaymentRange,
        value: string | number
    ) => {
        const nextRanges = ranges.map((range, currentIndex) =>
            currentIndex === rangeIndex ? { ...range, [key]: value } : range
        )
        formControl.setValue(`${root}.ranges` as Path<Contract>, nextRanges)
    }

    const addRange = () => {
        const lastRange = ranges[ranges.length - 1]
        const start = lastRange?.end_date
            ? dayjs(toDate(lastRange.end_date)).add(1, 'day')
            : dayjs()

        formControl.setValue(`${root}.ranges` as Path<Contract>, [
            ...ranges,
            {
                start_date: start,
                end_date: start.endOf('month'),
                price: 0,
            },
        ])
    }

    const removeRange = (rangeIndex: number) => {
        if (ranges.length <= 1) return
        formControl.setValue(
            `${root}.ranges` as Path<Contract>,
            ranges.filter((_, currentIndex) => currentIndex !== rangeIndex)
        )
    }

    return {
        formControl,
        root,
        paymentMode,
        ranges,
        updateRange,
        addRange,
        removeRange,
    }
}
